import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const InstitutionModal = ({ institution, isOpen, onClose, onToggleFavorite, isFavorite }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !institution) return null;

  const formatFees = (fees) => {
    if (fees === 0) return 'Gratuit';
    return `${fees?.toLocaleString('fr-MA')} MAD/an`;
  };

  const getTypeLabel = (type) => {
    const labels = {
      'universite-publique': 'Université Publique',
      'universite-privee': 'Université Privée',
      'ecole-ingenieur': 'École d\'Ingénieur',
      'ecole-commerce': 'École de Commerce',
      'institut-specialise': 'Institut Spécialisé',
      'ecole-art': 'École d\'Art et Design',
      'formation-professionnelle': 'Formation Professionnelle'
    };
    return labels?.[type] || type;
  };

  // Mock data for charts
  const admissionData = [
    { year: '2020', threshold: 14.2, applications: 1200, accepted: 180 },
    { year: '2021', threshold: 14.8, applications: 1350, accepted: 195 },
    { year: '2022', threshold: 15.1, applications: 1480, accepted: 210 },
    { year: '2023', threshold: 15.4, applications: 1620, accepted: 225 }
  ];

  const careerOutcomes = [
    { sector: 'Technologie', percentage: 35 },
    { sector: 'Finance', percentage: 25 },
    { sector: 'Consulting', percentage: 20 },
    { sector: 'Industrie', percentage: 15 },
    { sector: 'Autres', percentage: 5 }
  ];

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: 'Info' },
    { id: 'programs', label: 'Programmes', icon: 'BookOpen' },
    { id: 'admission', label: 'Admission', icon: 'Users' },
    { id: 'statistics', label: 'Statistiques', icon: 'BarChart3' },
    { id: 'careers', label: 'Débouchés', icon: 'TrendingUp' }
  ];

  const TabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">À propos de l'établissement</h3>
              <p className="text-muted-foreground leading-relaxed">
                {institution?.description || `${institution?.name} est un établissement d'enseignement supérieur reconnu au Maroc, offrant des formations de qualité dans plusieurs domaines. L'établissement se distingue par son approche pédagogique innovante et ses partenariats avec le monde professionnel.`}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-3">Informations générales</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Type:</span>
                    <span>{getTypeLabel(institution?.type)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ville:</span>
                    <span>{institution?.city}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Fondé en:</span>
                    <span>{institution?.founded || '1985'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Étudiants:</span>
                    <span>{institution?.studentCount || '2,500'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3">Services disponibles</h4>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <Icon 
                      name={institution?.hasScholarships ? 'Check' : 'X'} 
                      size={16} 
                      className={institution?.hasScholarships ? 'text-success mr-2' : 'text-muted-foreground mr-2'} 
                    />
                    <span>Bourses d'études</span>
                  </div>
                  <div className="flex items-center">
                    <Icon 
                      name={institution?.hasInternships ? 'Check' : 'X'} 
                      size={16} 
                      className={institution?.hasInternships ? 'text-success mr-2' : 'text-muted-foreground mr-2'} 
                    />
                    <span>Stages en entreprise</span>
                  </div>
                  <div className="flex items-center">
                    <Icon 
                      name={institution?.hasExchange ? 'Check' : 'X'} 
                      size={16} 
                      className={institution?.hasExchange ? 'text-success mr-2' : 'text-muted-foreground mr-2'} 
                    />
                    <span>Programmes d'échange</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Check" size={16} className="text-success mr-2" />
                    <span>Bibliothèque moderne</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'programs':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Programmes disponibles</h3>
              <div className="grid gap-4">
                {institution?.programs?.map((program, index) => (
                  <div key={index} className="border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-foreground mb-2">{program}</h4>
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          <span>Durée: 3 ans</span>
                          <span>Diplôme: Licence</span>
                          <span>Langue: Français/Arabe</span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        Détails
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'admission':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Conditions d'admission</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-3">Baccalauréats acceptés</h4>
                  <div className="space-y-2">
                    {institution?.acceptedBacTypes?.map((bac, index) => (
                      <div key={index} className="flex items-center">
                        <Icon name="Check" size={16} className="text-success mr-2" />
                        <span>{bac}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-3">Informations importantes</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Seuil minimum:</span>
                      <span className="font-medium">{institution?.previousThreshold || '12.00'}/20</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Frais d'inscription:</span>
                      <span className="font-medium">500 MAD</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Frais annuels:</span>
                      <span className="font-medium">{formatFees(institution?.fees)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <h4 className="font-medium mb-3">Processus de candidature</h4>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">1</div>
                    <div>
                      <p className="font-medium">Dépôt du dossier</p>
                      <p className="text-sm text-muted-foreground">Soumission en ligne ou sur place</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">2</div>
                    <div>
                      <p className="font-medium">Étude du dossier</p>
                      <p className="text-sm text-muted-foreground">Vérification des critères d'admission</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">3</div>
                    <div>
                      <p className="font-medium">Entretien/Test</p>
                      <p className="text-sm text-muted-foreground">Si requis selon le programme</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">4</div>
                    <div>
                      <p className="font-medium">Résultats</p>
                      <p className="text-sm text-muted-foreground">Notification par email/SMS</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'statistics':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Évolution des seuils d'admission</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={admissionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis domain={[12, 18]} />
                    <Tooltip 
                      formatter={(value) => [`${value}/20`, 'Seuil']}
                      labelFormatter={(label) => `Année ${label}`}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="threshold" 
                      stroke="var(--color-primary)" 
                      strokeWidth={2}
                      dot={{ fill: 'var(--color-primary)' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Candidatures vs Admissions</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={admissionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="applications" fill="var(--color-muted)" name="Candidatures" />
                    <Bar dataKey="accepted" fill="var(--color-primary)" name="Admis" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );

      case 'careers':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Débouchés professionnels</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={careerOutcomes} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 40]} />
                    <YAxis dataKey="sector" type="category" width={80} />
                    <Tooltip formatter={(value) => [`${value}%`, 'Pourcentage']} />
                    <Bar dataKey="percentage" fill="var(--color-secondary)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-3">Taux d'emploi</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">6 mois après diplôme:</span>
                    <span className="font-medium text-success">85%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">12 mois après diplôme:</span>
                    <span className="font-medium text-success">92%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Salaire moyen initial:</span>
                    <span className="font-medium">8,500 MAD</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3">Entreprises partenaires</h4>
                <div className="space-y-1">
                  <p className="text-sm">• OCP Group</p>
                  <p className="text-sm">• Attijariwafa Bank</p>
                  <p className="text-sm">• BMCE Bank</p>
                  <p className="text-sm">• Maroc Telecom</p>
                  <p className="text-sm">• Royal Air Maroc</p>
                  <p className="text-sm text-primary">+ 50 autres partenaires</p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-interactive w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div className="flex items-start space-x-4 flex-1">
            <div className="w-16 h-16 overflow-hidden rounded-lg flex-shrink-0">
              <Image
                src={institution?.image}
                alt={institution?.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-foreground mb-1">{institution?.name}</h2>
              <div className="flex items-center text-muted-foreground mb-2">
                <Icon name="MapPin" size={16} className="mr-1" />
                <span>{institution?.city}</span>
              </div>
              <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                {getTypeLabel(institution?.type)}
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onToggleFavorite(institution?.id)}
              className="p-2 hover:bg-muted rounded-lg transition-smooth"
            >
              <Icon 
                name="Heart" 
                size={20} 
                className={isFavorite ? 'text-red-500 fill-current' : 'text-muted-foreground'} 
              />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-smooth"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-smooth whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <TabContent />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              iconName="ExternalLink"
              iconPosition="left"
              onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(institution?.name)}`, '_blank')}
            >
              Site web
            </Button>
            <Button
              variant="outline"
              iconName="MapPin"
              iconPosition="left"
              onClick={() => window.open(`https://www.google.com/maps/search/${encodeURIComponent(institution?.name + ' ' + institution?.city)}`, '_blank')}
            >
              Localisation
            </Button>
          </div>
          <Button onClick={onClose}>
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstitutionModal;